<template>
    <div class="table-container w-full">
        <div class="list-top-container">
            <div class="list-search-container w-full mb-2 md:w-auto md:mb-0">
                <a-input-search
                    data-cy="uploads-cs-input-search"
                    @search="onSearch"
                    :placeholder="$t('globals.tables.search')"
                />
            </div>
        </div>
        <div class="list-table pt-2 md:pt-4 overflow-x-auto overflow-visible mt-1">
            <div class="w-full">
                <a-table
                    data-cy="upload-table"
                    :columns="getColumns"
                    :dataSource="data"
                    :rowKey="(record) => record.id"
                    :pagination="pagination"
                    :locale="locale"
                    :loading="loading"
                    @change="handleTableChange"
                    size="middle"
                    class="ant-table-striped"
                >
                    <template #bodyCell="{ column, record, text }">
                        <template v-if="column.key === 'state'">
                            <a-tag v-if="text == 'waiting'" color="blue">
                                {{ text }}
                            </a-tag>
                            <a-tag v-if="text == 'running'" color="orange">
                                {{ text }}
                            </a-tag>
                            <a-tag v-if="text == 'completed'" color="green">
                                {{ text }}
                            </a-tag>
                            <a-tag v-if="text == 'failed'" color="red">
                                {{ text }}
                            </a-tag>
                            <a-tag v-if="text == 'stopped'" color="yellow">
                                {{ text }}
                            </a-tag>
                        </template>
                        <template v-else-if="column.key === 'is_email_sent'">
                            <CheckOutlined v-if="text == '1'" class="text-default-green" />
                            <CloseOutlined v-if="text == '0'" class="text-default-red" />
                        </template>
                        <template v-else-if="column.key === 'created_at'">
                            <a-tooltip placement="right" :title="formatDate(text)">
                                <div>
                                    {{ timeAgo(text) }}
                                </div>
                            </a-tooltip>
                        </template>
                        <template v-else>
                            {{ text }}
                        </template>
                    </template>
                </a-table>
                <span v-if="data.length > 0" data-cy="page-item-range" class="page-item-range flex">
                    {{
                        $t('globals.tables.page_range', {
                            pi_start: pageItemStart,
                            pi_end: pageItemEnd,
                            total: pagination.total,
                        })
                    }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import { CheckOutlined, CloseOutlined } from '@ant-design/icons-vue';

export default {
    components: { CheckOutlined, CloseOutlined },
    data() {
        return {
            data: [],
            search_value: null,
            pagination: {
                pageSize: 10,
                showSizeChanger: false,
                total: 0,
            },
            locale: {
                emptyText: this.$t('globals.tables.no_data'),
            },
            loading: false,
            processing: false,
            current_filter: {},
            sorter: {},
            params: {},
            hover: { record: {}, hover: false },
            reload_uploads: {},
        };
    },
    mounted() {
        this.startCycle();
    },
    beforeDestroy() {
        clearInterval(this.reload_uploads);
    },
    computed: {
        getColumns() {
            return [
                {
                    title: this.$t('globals.tables.uploads.headers.company_name'),
                    key: 'name',
                    dataIndex: 'name',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.uploads.headers.file_name'),
                    key: 'file_name',
                    dataIndex: 'file_name',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.uploads.headers.file_import_type'),
                    key: 'file_import_type',
                    dataIndex: 'file_import_type',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.uploads.headers.state'),
                    key: 'state',
                    dataIndex: 'state',
                    sorter: true,
                    align: 'center',
                },
                {
                    title: this.$t('globals.tables.uploads.headers.is_email_sent'),
                    key: 'is_email_sent',
                    dataIndex: 'is_email_sent',
                    sorter: true,
                    align: 'center',
                },
                {
                    title: this.$t('globals.tables.uploads.headers.created_at'),
                    key: 'created_at',
                    dataIndex: 'created_at',
                    defaultSortOrder: 'descend',
                    sorter: true,
                },
            ];
        },
        pageItemStart() {
            let current_page = this.pagination.current;

            if (current_page === undefined) current_page = 1;

            return (current_page - 1) * this.pagination.pageSize + 1;
        },
        pageItemEnd() {
            let current_page = this.pagination.current,
                page_item_end = 0;

            if (current_page === undefined) current_page = 1;

            page_item_end = current_page * this.pagination.pageSize;

            if (this.pagination.total !== undefined && this.pagination.total < page_item_end) {
                return this.pagination.total;
            } else {
                return page_item_end;
            }
        },
    },
    methods: {
        startCycle() {
            this.fetch();

            this.reload_uploads = setInterval(() => {
                this.reload();
            }, 30000);
        },
        fetch(params = {}) {
            this.loading = true;
            this.params = params;

            this.$http
                .get('/api/uploads', { params: params })
                .then((response) => {
                    const pagination = { ...this.pagination };

                    // Read total count from server
                    pagination.total = response.data.meta.total_count;

                    this.loading = false;
                    this.data = response.data.data;
                    this.pagination = pagination;
                    this.selected_row_keys = [];
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVUPL01' }),
                        });
                    }
                });
        },
        handleTableChange(pagination, filters, sorter) {
            const pager = { ...this.pagination };

            pager.current = pagination.current;

            this.pagination = pager;
            this.current_filter = filters;
            this.sorter = sorter;

            this.fetch({
                ...this.params,
                results: pagination.pageSize,
                page: pagination.current,
                sortField: sorter.field,
                sortOrder: sorter.order,
                ...filters,
            });
        },
        onSearch(input) {
            let regex = /^[a-zA-Z0-9\s\(\)_-]+$/;

            if (regex.test(input) || input === '') {
                if (input === '') {
                    this.search_value = null;
                } else {
                    this.search_value = input;
                }

                this.pagination.current = 1;

                this.fetch({
                    ...this.params,
                    page: this.pagination.current,
                    search_value: this.search_value,
                });
            } else {
                this.$notification.error({
                    message: this.$t('globals.tables.invalid_characters'),
                });
            }
        },
        timeAgo(value) {
            let mjs = this.$dayjs(value);

            return mjs.isValid() ? mjs.fromNow() : '';
        },
        formatDate(datetime) {
            return this.$dayjs(datetime).format('YYYY-MM-DD HH:mm:ss');
        },
        reload() {
            const filters = this.current_filter;

            this.fetch({
                ...this.params,
                results: this.pagination.pageSize,
                page: this.pagination.current,
                sortField: this.sorter.field,
                sortOrder: this.sorter.order,
                ...filters,
            });
        },
    },
};
</script>
